import { AutocompleteValue, BaseTextFieldProps, SelectChangeEvent, TabsProps, TextFieldProps } from '@mui/material';
import { DateTimePickerProps, TimePickerProps } from '@mui/x-date-pickers';
import { isValid } from 'date-fns';
import { FieldConfig, useField } from 'formik';
import { ChangeEvent, FC, SyntheticEvent } from 'react';
import { AutocompleteProps, CheckBoxProps, CustomSliderProps, SelectBoxProps, SwitchProps } from '../components';

const getErrorProps = (touched: boolean, error?: string) =>
  touched && error ? { error: true, helperText: error } : {};

type InputTypes = BaseTextFieldProps | CheckBoxProps;
type Inputs<T extends InputTypes> = FieldConfig & T;
type ChangeEvents<T> = {
  onBeforeChange?: (value: T, nextValue: T) => void;
  onAfterChange?: (value: T, prevValue: T) => void;
};

export const withFormikInput =
  <T extends InputTypes>(FormInput: FC<T>) =>
  (props: Inputs<T>) => {
    const { name, inputProps = {} } = props;
    const [input, { error, touched }] = useField(name);

    return (
      <FormInput
        {...props}
        {...input}
        inputProps={{ ...inputProps, 'data-cy': name }}
        checked={input.value}
        {...getErrorProps(touched, error)}
      />
    );
  };

type Radio<T> = FieldConfig & T & ChangeEvents<T>;

export const withFormikRadio =
  <T,>(FormInput: FC<T>) =>
  (props: Radio<T>) => {
    const { name, type, onAfterChange, ...restProps } = props;
    const [input, { error, touched }, { setValue }] = useField(name);
    const onChangeWithType = async (event: ChangeEvent<HTMLInputElement>) => {
      let newValue: string | number | boolean = event.target.value;
      if (type === 'number') newValue = parseInt(event.target.value);
      if (type === 'boolean') newValue = event.target.value === 'true';
      await setValue(newValue);
      onAfterChange && onAfterChange(input.value, newValue as T);
    };

    return (
      <FormInput
        {...(restProps as Radio<T>)}
        {...input}
        checked={input.value}
        onChange={onChangeWithType}
        {...getErrorProps(touched, error)}
      />
    );
  };

type Tabs<T extends TabsProps> = FieldConfig & T;

export const withFormikTabs =
  <T extends TabsProps>(Tabs: FC<T>) =>
  (props: Tabs<T>) => {
    const { name } = props;
    const [{ value }, { error, touched }, { setTouched, setValue }] = useField(name);
    const onChangeHandler = (_: unknown, value: unknown) => {
      setValue(value, true);
      setTouched(true);
    };

    return <Tabs {...props} value={value} onChange={onChangeHandler} {...getErrorProps(touched, error)} />;
  };

export const withFormikSlider =
  (SliderComponent: FC<CustomSliderProps>) => (props: FieldConfig & CustomSliderProps) => {
    const [input, { error }, { setValue }] = useField(props.name);

    return (
      <SliderComponent
        {...input}
        {...props}
        error={!!error}
        helperText={error}
        onChange={(_, v) => setValue(v)}
      ></SliderComponent>
    );
  };

export const withFormikSelectBox =
  <T,>(SelectBox: FC<SelectBoxProps<T>>) =>
  (props: FieldConfig & SelectBoxProps<T> & ChangeEvents<T>) => {
    const { name, inputProps = {}, onBeforeChange, onAfterChange } = props;
    const [input, { value, error, touched }, { setTouched, setValue }] = useField(name);
    const onChangeHandler = async (e: SelectChangeEvent<T>) => {
      const { value: nextValue } = e.target ?? {};
      onBeforeChange && onBeforeChange(value, nextValue as T);
      await setValue(nextValue, true);
      await setTouched(true);
      onAfterChange && onAfterChange(nextValue as T, value);
    };
    return (
      <SelectBox
        {...props}
        {...input}
        onChange={onChangeHandler}
        inputProps={{ ...inputProps, 'data-cy': name }}
        {...getErrorProps(touched, error)}
      />
    );
  };

export const withFormikAutocomplete =
  <
    Value,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
  >(
    Autocomplete: FC<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>>,
  ) =>
  ({
    onBeforeChange,
    onAfterChange,
    name,
    ...props
  }: FieldConfig & ChangeEvents<Value> & AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) => {
    const [_, { value, error, touched }, { setTouched, setValue }] = useField(name);
    const onChangeHandler = async (
      e: SyntheticEvent,
      nextValue: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
    ) => {
      onBeforeChange && onBeforeChange(value, nextValue as Value);
      await setValue(nextValue, true);
      await setTouched(true);
      onAfterChange && onAfterChange(nextValue as Value, value);
    };
    return <Autocomplete {...props} value={value} onChange={onChangeHandler} {...getErrorProps(touched, error)} />;
  };

export const withFormikSwitch =
  (Switch: FC<SwitchProps>) => (props: FieldConfig & SwitchProps & ChangeEvents<unknown>) => {
    const { name, inputProps = {}, onBeforeChange, onAfterChange, ...switchProps } = props;
    const [{ value = false }, { error, touched }, { setTouched, setValue }] = useField(name);

    return (
      <Switch
        {...switchProps}
        inputProps={{ ...inputProps, 'data-cy': name } as SwitchProps['inputProps']}
        checked={value}
        onChange={async (_, checked) => {
          onBeforeChange && onBeforeChange(value, checked);
          await setValue(checked);
          await setTouched(true, true);
          onAfterChange && onAfterChange(checked, value);
        }}
        {...getErrorProps(touched, error)}
      />
    );
  };

type AmPm24hoursTime = {
  ampm: boolean;
  ampmInClock: boolean;
  format: string;
};

const detectAmPm24hoursTime = (): AmPm24hoursTime => {
  const language = navigator.language;
  const is12HourFormat = language.includes('en-') ?? false;

  return {
    ampm: is12HourFormat ?? false,
    ampmInClock: is12HourFormat ?? false,
    format: is12HourFormat ? 'h:mm aa' : 'HH:mm',
  };
};

type DateTimePicker = FieldConfig & DateTimePickerProps<Date>;

export const withFormikDateTimePicker = (Picker: FC<DateTimePicker>) => (props: DateTimePicker) => {
  const { name, slotProps: { textField: textFieldProps = {}, ...otherSlotProps } = {} } = props;
  const [input, { error, touched }, { setValue, setTouched }] = useField(name);

  return (
    <Picker
      {...props}
      {...input}
      value={input.value ? new Date(input.value) : null}
      onChange={(value: Date | null) => {
        const date = isValid(value) ? value?.toISOString() : value;
        setTouched(true);
        setValue(date, true);
      }}
      slotProps={{
        textField: {
          ...getErrorProps(touched, error),
          ...textFieldProps,
          inputProps: { 'data-cy': name },
        },
        ...otherSlotProps,
      }}
    />
  );
};

type TimePicker = FieldConfig & TimePickerProps<Date> & { size?: TextFieldProps['size'] };

export const withFormikTimePicker = (TimePicker: FC<TimePicker>) => (props: TimePicker) => {
  const { name, size = 'medium' } = props;
  const [input, { error, touched }, { setValue }] = useField(name);
  const { ampm, ampmInClock, format } = detectAmPm24hoursTime();

  return (
    <TimePicker
      {...props}
      {...input}
      value={input.value ? new Date(input.value) : null}
      ampm={ampm}
      ampmInClock={ampmInClock}
      format={format}
      onChange={(value: Date | null) => {
        const date = isValid(value) ? value?.toISOString() : value;
        setValue(date, true);
      }}
      slotProps={{
        textField: {
          ...getErrorProps(touched, error),
          inputProps: { 'data-cy': name },
          size,
        },
        field: {
          clearable: true,
        },
      }}
      sx={{ '.clearButton': { opacity: 1 } }}
    />
  );
};
